import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../auth/AuthProvider";
import ActionInbox, { type InboxItem } from "../components/ActionInbox";
import ActivityFeed from "../components/ActivityFeed";
import CoverageSparkline from "../components/CoverageSparkline";
import Drawer from "../components/Drawer";
import InboxPanel from "../components/InboxPanel";
import LivePills from "../components/LivePills";
import ReadinessBand from "../components/ReadinessBand";
import { FindingGlyph, OutcomeGlyph, VerdictShield } from "../glyphs";
import { api } from "../queries";
import { useUrlParam } from "../url";
import type { ReconcileReport } from "../types";

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const INBOX_LIMIT = 8;

export default function Now() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [drawer, setDrawer] = useUrlParam("drawer");
  const [reconcilePreview, setReconcilePreview] = useState<ReconcileReport | null>(null);

  const previewMutation = useMutation({
    mutationFn: () => api.reconcileRuns({ apply: false, action: "flag" }),
    onSuccess: (data) => setReconcilePreview(data),
  });
  const applyMutation = useMutation({
    mutationFn: () => api.reconcileRuns({ apply: true, action: "flag" }),
    onSuccess: () => {
      setReconcilePreview(null);
      queryClient.invalidateQueries();
    },
  });

  // The band leads with the latest full regression; with none, the latest run.
  const regressionQuery = useQuery({
    queryKey: ["latest-regression", "full_regression"],
    queryFn: () => api.listRuns(new URLSearchParams({ scope_kind: "full_regression", limit: "1" })),
  });
  const anyRunQuery = useQuery({
    queryKey: ["runs", "latest-any"],
    queryFn: () => api.listRuns(new URLSearchParams({ limit: "1" })),
    enabled: regressionQuery.isSuccess && (regressionQuery.data?.items.length ?? 0) === 0,
  });
  const latest = regressionQuery.data?.items[0] ?? anyRunQuery.data?.items[0];

  const readinessQuery = useQuery({
    queryKey: ["readiness", latest?.id],
    queryFn: () => api.getReadiness(latest!.id),
    enabled: Boolean(latest?.id),
  });

  const activeParams = useMemo(() => {
    const p = new URLSearchParams({ limit: "50" });
    for (const status of ["planning", "executing", "reducing"]) p.append("status", status);
    return p;
  }, []);
  const activeQuery = useQuery({
    queryKey: ["runs", "active", activeParams.toString()],
    queryFn: () => api.listRuns(activeParams),
  });

  const staleQuery = useQuery({
    queryKey: ["runs", "stale", "inbox"],
    queryFn: () => api.listRuns(new URLSearchParams({ stale: "true", limit: String(INBOX_LIMIT) })),
  });

  const pendingQuery = useQuery({
    queryKey: ["pending-reviews", "inbox"],
    queryFn: () => api.listPendingReviews(new URLSearchParams({ limit: String(INBOX_LIMIT) })),
  });

  const findingsQuery = useQuery({
    queryKey: ["findings", "ungraduated", "inbox"],
    queryFn: () => api.listUngraduatedFindings(new URLSearchParams({ limit: String(INBOX_LIMIT) })),
  });

  const [weekStart] = useState(() => new Date(Date.now() - WEEK_MS).toISOString());
  const trendQuery = useQuery({
    queryKey: ["coverage-trend", "now", weekStart],
    queryFn: () =>
      api.getCoverageTrend(new URLSearchParams({ created_after: weekStart, limit: "50" })),
  });

  const readiness = readinessQuery.data;

  const inbox = useMemo<InboxItem[]>(() => {
    const items: InboxItem[] = [];

    // Blocked work first: it is the thing that stops a release outright.
    if (readiness && readiness.current.blocked_count > 0) {
      const run = readiness.current;
      items.push({
        id: `blocked-${run.run_id}`,
        title: `${run.blocked_count} blocked target${run.blocked_count === 1 ? "" : "s"} in run #${run.run_number}`,
        detail: "No executable result — a fixture or a human is missing.",
        to: `/runs/${run.run_id}?tab=gaps`,
        glyph: <OutcomeGlyph outcome="error" size="sm" />,
        rank: 5,
      });
    }

    // Pending verdicts, lowest confidence first: the least certain call is the
    // one most likely to be wrong.
    for (const verdict of pendingQuery.data?.items ?? []) {
      const confidence = verdict.confidence ?? 0;
      items.push({
        id: `verdict-${verdict.id}`,
        title: `${verdict.verdict.replaceAll("_", " ")} on run #${verdict.run_number}`,
        detail: `${verdict.result_outcome} · confidence ${Math.round(confidence * 100)}%`,
        to: "/triage",
        glyph: <VerdictShield verdict={verdict.verdict === "real_regression" ? "no_go" : null} size="sm" />,
        rank: 10 + Math.round(confidence * 10),
      });
    }

    // Stale runs: stuck waiting on a message that never arrived.
    for (const run of staleQuery.data?.items ?? []) {
      items.push({
        id: `stale-${run.id}`,
        title: `Run #${run.run_number} has gone quiet`,
        detail: `${run.scope_kind.replaceAll("_", " ")} · ${run.scope_ref} — reconcile or orphan it.`,
        to: `/runs/${run.id}`,
        glyph: <VerdictShield verdict={null} size="sm" />,
        rank: 20,
      });
    }

    // Spec gaps ready to graduate — the intake funnel's next step.
    for (const finding of findingsQuery.data?.items ?? []) {
      items.push({
        id: `finding-${finding.id}`,
        title: finding.description,
        detail: finding.proposed_spec_section
          ? `Spec gap → ${finding.proposed_spec_section}`
          : "Spec gap ready to graduate.",
        to: "/exploration",
        glyph: <FindingGlyph finding={finding.finding_type} size="sm" />,
        rank: 30,
      });
    }

    return items;
  }, [readiness, pendingQuery.data, staleQuery.data, findingsQuery.data]);

  const trendItems = trendQuery.data?.items ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">Now</h1>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setDrawer("activity")}
          >
            Activity
          </button>
          <details className="dropdown dropdown-end">
            <summary className="btn btn-ghost btn-sm">Health</summary>
            <div className="dropdown-content z-20 mt-2 w-80 rounded-box bg-base-100 p-4 shadow">
              <h2 className="font-semibold mb-1">Reconcile</h2>
              <p className="text-xs opacity-70 mb-3">
                Complete runs whose scenarios finished but whose reduce message was lost, and flag
                runs gone quiet past their policy threshold. Preview first; applying flags stale
                runs (it never orphans).
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  className="btn btn-xs"
                  disabled={previewMutation.isPending}
                  onClick={() => previewMutation.mutate()}
                >
                  {previewMutation.isPending ? "Previewing…" : "Preview"}
                </button>
                {reconcilePreview && (
                  <button
                    type="button"
                    className="btn btn-xs btn-primary"
                    disabled={applyMutation.isPending}
                    onClick={() => applyMutation.mutate()}
                  >
                    {applyMutation.isPending ? "Applying…" : "Apply"}
                  </button>
                )}
              </div>
              {reconcilePreview && (
                <p className="text-xs mt-2">
                  {reconcilePreview.counts.completed} to complete · {reconcilePreview.counts.reduced}{" "}
                  to reduce · {reconcilePreview.counts.flagged} stale ·{" "}
                  {reconcilePreview.counts.skipped} waiting on humans
                </p>
              )}
              {(previewMutation.isError || applyMutation.isError) && (
                <p className="text-error text-xs mt-2">Reconcile failed.</p>
              )}
            </div>
          </details>
        </div>
      </div>

      {readinessQuery.isLoading && !readiness && <span className="loading loading-spinner" />}
      {readiness ? (
        <ReadinessBand readiness={readiness} />
      ) : (
        !readinessQuery.isLoading && (
          <div className="rounded-lg bg-base-200 p-6 text-center space-y-2">
            <p className="opacity-70">No runs yet.</p>
            <p className="text-sm opacity-50">
              Runs are planned by Mission Control. Once one is planned, its readiness
              verdict appears here.
            </p>
            <Link to="/runs" className="btn btn-sm">
              Go to Runs
            </Link>
          </div>
        )
      )}

      <section>
        <h2 className="font-semibold mb-2">Live now</h2>
        <LivePills runs={activeQuery.data?.items ?? []} />
      </section>

      <section>
        <h2 className="font-semibold mb-2">Your inbox</h2>
        <InboxPanel actor={user?.email} />
      </section>

      <section>
        <h2 className="font-semibold mb-2">Needs a human</h2>
        <ActionInbox items={inbox} />
      </section>

      <section className="card bg-base-200">
        <div className="card-body gap-2">
          <h2 className="card-title text-base">Coverage movement</h2>
          <CoverageSparkline entries={trendItems} />
        </div>
      </section>

      <Drawer open={drawer === "activity"} onClose={() => setDrawer("")} title="Activity">
        <ActivityFeed />
      </Drawer>
    </div>
  );
}
